/**
 * Hotels API — client functions for non-stream endpoints.
 *
 * Minimal fetch wrappers following the same token/auth pattern as useHotelSearchStream.
 * All functions return typed responses or throw on error.
 */

import { getHotelApiToken } from '../../../core/auth/getHotelApiToken';
import { availabilityUrl, bookingsUrl, hotelInquiryUrl } from './hotels.endpoints';
import { HotelBookingRejectedError, HotelBookingUnverifiedError } from './hotelBookingErrors';
import { HOTEL_REQUEST_TIMEOUTS_MS, fetchWithTimeout } from './requestTimeouts';
import store from 'storeClient';
import type {
  HotelAvailabilityRequest,
  HotelAvailabilityResponse,
  HotelBookingRequest,
  HotelBookingOutcome,
  HotelBookingResponse,
  NonRefundableInquiryRequest,
  NonRefundableInquiryResponse,
} from './hotels.types';

/**
 * Calls POST /api/v1/hotels/availability to verify that a previously-seen
 * offer/rate is still valid for rebooking.
 *
 * @returns The availability token on success (rate is still valid).
 * @throws On non-2xx responses or missing auth token.
 */
export async function revalidateHotelAvailability(
  request: HotelAvailabilityRequest,
): Promise<HotelAvailabilityResponse> {
  const token = await getHotelApiToken();

  if (!token) {
    throw new Error('hotels.api.missingAuthToken');
  }

  const response = await fetchWithTimeout(
    availabilityUrl(),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    },
    HOTEL_REQUEST_TIMEOUTS_MS.availability,
  );

  if (!response.ok) {
    throw new Error(`hotels.api.availabilityFailed:${response.status}`);
  }

  const body: HotelAvailabilityResponse = await response.json();

  if (!body.availabilityToken) {
    throw new Error('hotels.api.invalidAvailabilityResponse');
  }

  return body;
}

function readErrorCode(body: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(body);
    const code = (parsed as { code?: unknown })?.code;
    return typeof code === 'string' ? code : undefined;
  } catch {
    return undefined;
  }
}

/** Fallback for backends that do not send `outcome` yet. */
function outcomeFromStatus(status: string | undefined): HotelBookingOutcome {
  const code = status?.toUpperCase();
  if (code === 'OK' || code === 'RR') return 'confirmed';
  if (code === 'CX' || code === 'ER') return 'rejected';
  return 'pending';
}

export async function createHotelBooking(
  request: HotelBookingRequest,
): Promise<HotelBookingResponse> {
  const token = await getHotelApiToken();

  if (!token) {
    throw new Error('hotels.api.missingAuthToken');
  }

  let response: Response;
  try {
    response = await fetchWithTimeout(
      bookingsUrl(),
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
      HOTEL_REQUEST_TIMEOUTS_MS.booking,
    );
  } catch {
    // Cut or timed out: the request may have reached the backend and created the booking.
    throw new HotelBookingUnverifiedError();
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    const code = readErrorCode(errorText);
    if (code === 'BOOKING_REJECTED') {
      throw new HotelBookingRejectedError();
    }
    if (code === 'BOOKING_OUTCOME_UNKNOWN' || response.status === 504) {
      throw new HotelBookingUnverifiedError();
    }
    throw new Error(errorText || `hotels.api.bookingFailed:${response.status}`);
  }

  const body: HotelBookingResponse = await response.json();

  if (!body.locator) {
    throw new Error('hotels.api.invalidBookingResponse');
  }

  return { ...body, outcome: body.outcome ?? outcomeFromStatus(body.status) };
}

export async function createNonRefundableInquiry(
  request: NonRefundableInquiryRequest,
): Promise<NonRefundableInquiryResponse> {
  const appState = store.getState().applicationState;
  const token: string | undefined = appState?.token;

  if (!token) {
    throw new Error('hotels.api.missingAuthToken');
  }

  const body = {
    agenciaId: appState?.applicationId,
    nombreAgencia: appState?.nombre,
    paisId: appState?.paisId,
    ...request,
  };

  const response = await fetchWithTimeout(
    hotelInquiryUrl(),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    HOTEL_REQUEST_TIMEOUTS_MS.inquiry,
  );

  if (response.status !== 201) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `hotels.api.inquiryFailed:${response.status}`);
  }

  return response.json() as Promise<NonRefundableInquiryResponse>;
}
